/* global React, useState, Icon, Card, Button, Field, Input, Select, Currency, Modal, EmptyState, toast, fmt, t, PlummeI18n */
// ============================================================
// PLUMME — AI Rådgiver: budsjett mot faktisk
// ============================================================
// Siden orkestrerer bare. Regnestykket ligger i services/aiAdvisorPlan.jsx.

function AiBudgetRow({ row, monthsElapsed }) {
  const sign = row.variance > 0 ? '+' : row.variance < 0 ? '−' : '';
  return <React.Fragment>
    <div className="aii-budget-row">
      <span className="aii-budget-cat">
        {row.name}
        {!row.budgeted && <span className="aii-budget-tag">{t('adv.plan.unbudgeted')}</span>}
      </span>
      <span>{fmt(row.budgetToDate)}</span>
      <span>{fmt(row.actual)}</span>
      <span style={{ color: row.variance === 0 ? 'var(--muted)' : row.unfavourable ? 'var(--error)' : 'var(--success)' }}>
        {sign}{fmt(Math.abs(row.variance))}
      </span>
    </div>
    {row.material && <div className="aii-budget-note">
      <Icon name="sparkle" size={13} />
      {row.budgeted
        ? t(row.unfavourable ? 'adv.plan.noteWorse' : 'adv.plan.noteBetter', {
          name: row.name, amount: fmt(Math.abs(row.variance)), months: monthsElapsed,
        })
        : t('adv.plan.noteUnbudgeted', { name: row.name, amount: fmt(row.actual) })}
    </div>}
  </React.Fragment>;
}

const AII_GOAL_METRICS = ['buffer', 'income', 'result', 'margin'];
const AII_EVENT_KINDS = ['salary', 'tax', 'supplier', 'investment', 'loan', 'other'];
const aiiGoalValue = (goal, value) => goal.metric === 'margin'
  ? `${Number(value).toLocaleString(PlummeI18n.getLocale(), { maximumFractionDigits: 1 })} %`
  : fmt(value);

function AiGoalCard({ goal, onEdit, onDelete, onComplete }) {
  const pct = goal.progress == null ? 0 : Math.round(goal.progress * 100);
  const tone = goal.completed ? 'green' : goal.needsReview ? 'amber' : goal.reached ? 'green' : goal.overdue ? 'red' : goal.onTrack === false ? 'amber' : 'plum';
  return <div className="aii-goal-card">
    <div className="aii-goal-head">
      <span className={'aii-goal-status tone-' + tone}>
        {goal.completed ? t('adv.plan.goalCompleted')
          : goal.needsReview ? t('adv.plan.goalNeedsReview')
          : goal.reached ? t('adv.plan.goalReached')
          : goal.overdue ? t('adv.plan.goalOverdue')
            : goal.onTrack == null ? t('adv.plan.goalTracking')
              : goal.onTrack ? t('adv.plan.goalOnTrack') : t('adv.plan.goalBehind')}
      </span>
      <div className="row gap-6">
        <button type="button" className="aii-goal-mini" onClick={() => onComplete(goal)} aria-label={t(goal.completed ? 'adv.plan.goalReopen' : 'adv.plan.goalComplete')} title={t(goal.completed ? 'adv.plan.goalReopen' : 'adv.plan.goalComplete')}><Icon name={goal.completed ? 'refresh' : 'check'} size={14} /></button>
        <button type="button" className="aii-goal-mini" onClick={() => onEdit(goal)} aria-label={t('sale.edit')}><Icon name="pencil" size={14} /></button>
        <button type="button" className="aii-goal-mini" onClick={() => onDelete(goal)} aria-label={t('list.delete')}><Icon name="trash" size={14} /></button>
      </div>
    </div>
    <div className="aii-goal-title">{goal.title}</div>
    <div className="aii-goal-track"><div className={'aii-goal-fill tone-' + tone} style={{ width: pct + '%' }} /></div>
    <div className="aii-goal-figs">
      <div><span className="lbl" data-help-title={t('adv.plan.goalNow')} data-help={t('adv.help.goalNow')}>{t('adv.plan.goalNow')}</span><b>{aiiGoalValue(goal, goal.current)}</b></div>
      <div><span className="lbl" data-help-title={t('adv.plan.goalTarget')} data-help={t('adv.help.goalTarget')}>{t('adv.plan.goalTarget')}</span><b>{aiiGoalValue(goal, goal.target)}</b></div>
      <div><span className="lbl" data-help={t('helpTopics.goal.body')} data-help-title={t('helpTopics.goal.title')}>{t('adv.plan.goalDeadline')}</span><b>{goal.deadline}</b></div>
      <div><span className="lbl">{t('adv.plan.goalDaysLeft')}</span><b>{goal.daysLeft == null ? '—' : goal.daysLeft}</b></div>
    </div>
    <p className="t-xs muted" style={{ marginTop: 8 }}>{t('adv.plan.goalMeasured', { metric: t('adv.plan.metric.' + goal.metric) })} · {t('adv.plan.goalFromYear', { year: goal.startYear })}</p>
  </div>;
}

function AiGoalModal({ goal, year, onClose, onSaved }) {
  const [title, setTitle] = useState(goal?.title || '');
  const [metric, setMetric] = useState(goal?.metric || 'buffer');
  const [target, setTarget] = useState(goal?.target ? String(goal.target) : '');
  const [deadline, setDeadline] = useState(goal?.deadline || '');
  const [busy, setBusy] = useState(false);
  const valid = title.trim() && Number(target) > 0 && deadline;
  const save = async () => {
    setBusy(true);
    try {
      await window.PlummeBackend.saveAdvisorGoal({ ...(goal || {}), startYear: goal?.startYear || year, title: title.trim(), metric, target: Number(target), deadline });
      toast(t('adv.plan.goalSaved'), 'success');
      onSaved();
    } catch (error) {
      toast(error?.message || t('adv.plan.goalSaveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };
  return <Modal open onClose={onClose} max={480} title={goal ? t('adv.plan.goalEdit') : t('adv.plan.goalNew')}
    footer={<React.Fragment><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <Button disabled={!valid || busy} onClick={save}>{busy ? t('adv.plan.saving') : t('adv.plan.goalSave')}</Button></React.Fragment>}>
    <div className="col gap-14" style={{ paddingBottom: 4 }}>
      <Field label={t('adv.plan.goalTitleLabel')}><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('adv.plan.goalTitlePh')} /></Field>
      {/* Målet måles mot bokførte tall. Brukeren velger hvilket tall, ikke hva det er. */}
      <Field label={t('adv.plan.goalMetricLabel')} hint={t('adv.plan.goalMetricHint')} help={t('helpTopics.goal.body')} helpTitle={t('helpTopics.goal.title')}>
        <Select value={metric} onChange={(e) => setMetric(e.target.value)}>
          {AII_GOAL_METRICS.map((m) => <option key={m} value={m}>{t('adv.plan.metric.' + m)}</option>)}
        </Select>
      </Field>
      <Field label={metric === 'margin' ? t('adv.plan.goalTargetPct') : t('adv.plan.goalTargetAmount')} help={t('helpTopics.goal.body')} helpTitle={t('helpTopics.goal.title')}>
        {metric === 'margin'
          ? <Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="0" />
          : <Currency value={target} onChange={(e) => setTarget(e.target.value)} placeholder="0" />}
      </Field>
      <Field label={t('adv.plan.goalDeadline')} help={t('helpTopics.goal.body')} helpTitle={t('helpTopics.goal.title')}><Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} /></Field>
    </div>
  </Modal>;
}

function AiEventModal({ event, onClose, onSaved }) {
  const [label, setLabel] = useState(event?.label || '');
  const [purpose, setPurpose] = useState(event?.purpose || '');
  const [kind, setKind] = useState(event?.kind || 'other');
  const [date, setDate] = useState(event?.date || '');
  const [amount, setAmount] = useState(event?.amount ? String(Math.abs(event.amount)) : '');
  const [direction, setDirection] = useState(event && event.amount > 0 ? 'in' : 'out');
  const [assetGroup, setAssetGroup] = useState(event?.assetGroup || '');
  const [assetTaxCost, setAssetTaxCost] = useState(event?.assetTaxCost ? String(event.assetTaxCost) : '');
  const [assetBusinessUsePct, setAssetBusinessUsePct] = useState(event?.assetBusinessUsePct ? String(event.assetBusinessUsePct) : '');
  const [assetUsefulLifeYears, setAssetUsefulLifeYears] = useState(event?.assetUsefulLifeYears ? String(event.assetUsefulLifeYears) : '');
  const [assetAcquiredDate, setAssetAcquiredDate] = useState(event?.assetAcquiredDate || '');
  const [busy, setBusy] = useState(false);
  const valid = label.trim() && date && Number(amount) > 0;
  const save = async () => {
    setBusy(true);
    try {
      const signed = direction === 'out' ? -Math.abs(Number(amount)) : Math.abs(Number(amount));
      await window.PlummeBackend.savePlannedCashEvent({ ...(event || {}), label: label.trim(), purpose: purpose.trim(), kind, date, amount: signed,
        assetGroup, assetTaxCost: Number(assetTaxCost) || null, assetBusinessUsePct: Number(assetBusinessUsePct) || null,
        assetUsefulLifeYears: Number(assetUsefulLifeYears) || null, assetAcquiredDate });
      toast(t('adv.plan.eventSaved'), 'success');
      onSaved();
    } catch (error) {
      toast(error?.message || t('adv.plan.eventSaveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };
  return <Modal open onClose={onClose} max={480} title={event ? t('adv.plan.eventEdit') : t('adv.plan.eventNew')}
    footer={<React.Fragment><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <Button disabled={!valid || busy} onClick={save} data-help={t('helpTopics.plannedCash.body')} data-help-title={t('helpTopics.plannedCash.title')}>{busy ? t('adv.plan.saving') : t('adv.plan.eventSave')}</Button></React.Fragment>}>
    <div className="col gap-14" style={{ paddingBottom: 4 }}>
      <Field label={t('adv.plan.eventLabel')} help={t('helpTopics.plannedCash.body')} helpTitle={t('helpTopics.plannedCash.title')}><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('adv.plan.eventLabelPh')} /></Field>
      {kind === 'investment' && <Field label={t('adv.tax.investment.purposeLabel')} hint={t('adv.tax.investment.purposeHint')} help={t('helpTopics.plannedCash.body')} helpTitle={t('helpTopics.plannedCash.title')}>
        <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} maxLength={240} placeholder={t('adv.tax.investment.purposePlaceholder')} />
      </Field>}
      <Field label={t('adv.plan.eventKind')} help={t('helpTopics.plannedCash.body')} helpTitle={t('helpTopics.plannedCash.title')}>
        <Select value={kind} onChange={(e) => setKind(e.target.value)}>
          {AII_EVENT_KINDS.map((k) => <option key={k} value={k}>{t('adv.plan.kind.' + k)}</option>)}
        </Select>
      </Field>
      <Field label={t('adv.plan.eventDate')} help={t('helpTopics.plannedCash.body')} helpTitle={t('helpTopics.plannedCash.title')}><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      <Field label={t('adv.plan.eventDirection')} hint={t('adv.plan.eventDirectionHint')} help={t('helpTopics.plannedCash.body')} helpTitle={t('helpTopics.plannedCash.title')}>
        <Select value={direction} onChange={(e) => setDirection(e.target.value)}>
          <option value="out">{t('adv.plan.directionOut')}</option>
          <option value="in">{t('adv.plan.directionIn')}</option>
        </Select>
      </Field>
      <Field label={t('adv.plan.eventAmount')} help={t('helpTopics.plannedCash.body')} helpTitle={t('helpTopics.plannedCash.title')}><Currency value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" /></Field>
      {kind === 'investment' && <React.Fragment>
        <p className="t-sm muted">{t('adv.tax.investment.taxDetailsHint')}</p>
        <Field label={t('adv.tax.investment.assetGroup')} help={t('helpTopics.taxAsset.body')} helpTitle={t('helpTopics.taxAsset.title')}><Select value={assetGroup} onChange={(e) => setAssetGroup(e.target.value)}>
          <option value="">{t('adv.tax.investment.selectGroup')}</option>
          <option value="a">{t('adv.tax.investment.groupA')}</option>
          <option value="d">{t('adv.tax.investment.groupD')}</option>
          <option value="other">{t('adv.tax.investment.groupOther')}</option>
        </Select></Field>
        <Field label={t('adv.tax.investment.taxCost')} hint={t('adv.tax.investment.taxCostHint')}><Currency value={assetTaxCost} onChange={(e) => setAssetTaxCost(e.target.value)} placeholder="0" /></Field>
        <Field label={t('adv.tax.investment.businessUse')}><Input type="number" min="1" max="100" value={assetBusinessUsePct} onChange={(e) => setAssetBusinessUsePct(e.target.value)} placeholder="100" /></Field>
        <Field label={t('adv.tax.investment.lifeYears')} help={t('helpTopics.usefulLife.body')} helpTitle={t('helpTopics.usefulLife.title')}><Input type="number" min="0.1" step="0.1" value={assetUsefulLifeYears} onChange={(e) => setAssetUsefulLifeYears(e.target.value)} placeholder="3" /></Field>
        <Field label={t('adv.tax.investment.acquiredDate')} hint={t('adv.tax.investment.acquiredDateHint')}><Input type="date" value={assetAcquiredDate} onChange={(e) => setAssetAcquiredDate(e.target.value)} /></Field>
      </React.Fragment>}
    </div>
  </Modal>;
}

function AiPlanTab({ year, onNav }) {
  const [dlg, setDlg] = useState(null);
  const [tick, setTick] = useState(0);
  const [forecastOpen, setForecastOpen] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const model = React.useMemo(
    () => window.PlummeBackend?.getAiAdvisorPlanModel?.({ year }) || null,
    [year, tick, window.PlummeBackend?.appState],
  );
  const refresh = () => { setDlg(null); setTick((n) => n + 1); };
  const advisor = forecastOpen ? window.PlummeBackend?.getAiAdvisorModel?.({ year }) : null;
  const baseForecast = advisor?.forecast || [];
  const planned = forecastOpen && globalThis.PlummeAiAdvisorPlan
    ? globalThis.PlummeAiAdvisorPlan.applyEventsToForecast({ forecast: baseForecast, events: model?.events || [], year })
    : { forecast: baseForecast, applied: [] };
  const forecastChange = planned.forecast.length && baseForecast.length
    ? planned.forecast[planned.forecast.length - 1].balance - baseForecast[baseForecast.length - 1].balance : 0;
  const removeGoal = async (goal) => {
    await window.PlummeBackend.deleteAdvisorGoal(goal.id);
    toast(t('adv.plan.goalDeleted'), 'info');
    refresh();
  };
  const toggleGoalComplete = async (goal) => {
    try {
      await window.PlummeBackend.saveAdvisorGoal({ ...goal, completedAt: goal.completed ? null : new Date().toISOString() });
      refresh();
    } catch (error) {
      toast(error?.message || t('adv.plan.goalSaveFailed'), 'error');
    }
  };
  const removeEvent = async (event) => {
    await window.PlummeBackend.deletePlannedCashEvent(event.id);
    toast(t('adv.plan.eventDeleted'), 'info');
    refresh();
  };
  const goalsAndEvents = model && <React.Fragment>
    <section className="aii-plan-section">
      <window.AiSecHead icon="sparkle" title={t('adv.plan.goalsTitle')} sub={t('adv.plan.goalsSub')} help={t('adv.plan.goalsHelp')}
        right={<Button size="sm" icon="plus" onClick={() => setDlg({ type: 'goal' })}>{t('adv.plan.goalNew')}</Button>} />
      <Card className="aii-plan-surface">
      {model.goals.length
        ? <div className="aii-goal-grid">
          {model.goals.map((goal) => <AiGoalCard key={goal.id} goal={goal} onEdit={(g) => setDlg({ type: 'goal', goal: g })} onDelete={removeGoal} onComplete={toggleGoalComplete} />)}
        </div>
        : <EmptyState noVisual acc="sparkle" title={t('adv.plan.goalsEmptyTitle')} text={t('adv.plan.goalsEmptyText')} compact />}
      </Card>
    </section>

    <section className="aii-plan-section">
      <window.AiSecHead icon="calendar" title={t('adv.plan.eventsTitle')} sub={t('adv.plan.eventsSub')} help={t('adv.plan.eventsHelp')}
        right={<div className="row gap-8 wrap">
          <Button size="sm" icon="trendUp" onClick={() => setForecastOpen(true)}>{t('adv.plan.viewForecast')}</Button>
          <Button size="sm" icon="plus" onClick={() => setDlg({ type: 'event' })}>{t('adv.plan.eventNew')}</Button>
        </div>} />
      <Card className="aii-plan-surface">
      {model.events.length
        ? <div className="aii-timeline">
          {model.events.map((event) => <div key={event.id} className="aii-timeline-item">
            <div className="aii-timeline-dot" />
            <div className="aii-timeline-body">
              <div className="aii-timeline-top">
                <span className="aii-timeline-date">{event.date}</span>
                <span className="aii-timeline-kind">{t('adv.plan.kind.' + event.kind)}</span>
              </div>
              <div className="aii-timeline-label">{event.label}</div>
              <div className="aii-timeline-amt" style={{ color: event.amount < 0 ? 'var(--error)' : 'var(--success)' }}>
                {event.amount > 0 ? '+' : '−'}{fmt(Math.abs(event.amount))}
              </div>
            </div>
            <div className="row gap-6">
              <button type="button" className="aii-goal-mini" onClick={() => setDlg({ type: 'event', event })} aria-label={t('sale.edit')}><Icon name="pencil" size={14} /></button>
              <button type="button" className="aii-goal-mini" onClick={() => removeEvent(event)} aria-label={t('list.delete')}><Icon name="trash" size={14} /></button>
            </div>
          </div>)}
        </div>
        : <EmptyState noVisual acc="sparkle" title={t('adv.plan.eventsEmptyTitle')} text={t('adv.plan.eventsEmptyText')} compact />}
      <p className="t-xs muted" style={{ marginTop: 10 }}>{t('adv.plan.eventsNote')}</p>
      </Card>
    </section>

    <Modal open={forecastOpen} onClose={() => setForecastOpen(false)} title={t('adv.plan.forecastTitle')}
      max={1400} maxHeight="88vh" portal center className="aii-plan-forecast-modal"
      footer={<Button onClick={() => setForecastOpen(false)}>{t('common.close')}</Button>}>
      {baseForecast.length ? <React.Fragment>
        <p className="aii-plan-forecast-intro">{t('adv.plan.forecastIntro')}</p>
        <div className="aii-plan-forecast-chart"><window.AiCashflowChart data={baseForecast} dataAfter={planned.forecast}
          buffer={advisor?.buffer || 0} afterLabel={t('adv.plan.forecastWithEvents')} /></div>
        <div className="aii-cf-legend aii-plan-forecast-legend">
          <span className="aii-cf-leg"><span className="aii-cf-swatch" style={{ background: 'var(--border-strong)' }} />{t('adv.plan.forecastWithoutEvents')}</span>
          <span className="aii-cf-leg"><span className="aii-cf-swatch" style={{ background: 'var(--success)' }} />{t('adv.plan.forecastWithEvents')}</span>
        </div>
        <p className="aii-plan-forecast-change">{t('adv.plan.forecastChange')}: <strong className={forecastChange > 0 ? 'positive' : forecastChange < 0 ? 'negative' : ''}>{forecastChange > 0 ? '+' : forecastChange < 0 ? '−' : ''}{fmt(Math.abs(forecastChange))}</strong></p>
        <p className="t-xs muted">{t('adv.plan.forecastScope')}</p>
      </React.Fragment> : <EmptyState noVisual acc="chart" title={t('adv.cf.emptyTitle')} text={t('adv.cf.emptyText')} compact />}
    </Modal>

    {dlg?.type === 'goal' && <AiGoalModal goal={dlg.goal} year={year} onClose={() => setDlg(null)} onSaved={refresh} />}
    {dlg?.type === 'event' && <AiEventModal event={dlg.event} onClose={() => setDlg(null)} onSaved={refresh} />}
  </React.Fragment>;
  if (!model) return null;
  // Mål og planlagte hendelser står uavhengig av om det finnes et budsjett — de er
  // brukerens egne, og en tom budsjettmelding skal ikke skjule dem.
  if (!model.hasBudget) {
    return <React.Fragment>
      {goalsAndEvents}
      <section className="aii-plan-section">
        <EmptyState noVisual acc="sparkle" title={t('adv.plan.noBudgetTitle')} text={t('adv.plan.noBudgetText')} compact
          action={<Button icon="arrowRight" onClick={() => onNav && onNav('budsjett')}>{t('adv.plan.goToBudget')}</Button>} />
      </section>
    </React.Fragment>;
  }
  if (model.monthsElapsed === 0) {
    return <React.Fragment>
      {goalsAndEvents}
      <section className="aii-plan-section">
        <EmptyState noVisual acc="sparkle" title={t('adv.plan.notStartedTitle')} text={t('adv.plan.notStartedText', { year: model.year })} compact />
      </section>
    </React.Fragment>;
  }
  const income = model.totals.incomeActual - model.totals.incomeBudgetToDate;
  const cost = model.totals.costActual - model.totals.costBudgetToDate;
  return <React.Fragment>
    {goalsAndEvents}
    <section className="aii-plan-section">
      <window.AiSecHead icon="calculator" title={t('adv.plan.title')}
        sub={t('adv.plan.sub', { year: model.year, months: model.monthsElapsed })}
        help={t('adv.plan.help')} />
      <Card><div className="aii-plan-tot">
        <div className={'aii-plan-tot-box ' + (income < 0 ? 'bad' : income > 0 ? 'good' : 'neutral')}>
          <span className="lbl" data-help-title={t('adv.plan.incomeVariance')} data-help={t('adv.help.incomeVariance')}>{t('adv.plan.incomeVariance')}</span>
          <span className="val">{income >= 0 ? '+' : '−'}{fmt(Math.abs(income))}</span>
          <span className="note">{t(income < 0 ? 'adv.plan.belowPlan' : income > 0 ? 'adv.plan.abovePlan' : 'adv.plan.goalOnTrack')}</span>
        </div>
        <div className={'aii-plan-tot-box ' + (cost > 0 ? 'bad' : cost < 0 ? 'good' : 'neutral')}>
          <span className="lbl" data-help-title={t('adv.plan.costVariance')} data-help={t('adv.help.costVariance')}>{t('adv.plan.costVariance')}</span>
          <span className="val">{cost >= 0 ? '+' : '−'}{fmt(Math.abs(cost))}</span>
          <span className="note">{t(cost > 0 ? 'adv.plan.overPlan' : cost < 0 ? 'adv.plan.underPlan' : 'adv.plan.goalOnTrack')}</span>
        </div>
      </div>
      {/* Inntekts- og kostnadsavvik står hver for seg med vilje. Å slå dem sammen til
          «du ligger X over budsjett» skjuler at man kan tape på inntekten og spare på
          kostnaden samtidig, og likevel havne på null. */}
      <p className="t-xs muted" style={{ marginTop: 10 }}>{t('adv.plan.separateNote')}</p>
      </Card>
    </section>

    <section className="aii-plan-section">
      <window.AiSecHead icon="sliders" title={t('adv.plan.tableTitle')} sub={t('adv.plan.tableSub', { months: model.monthsElapsed })} help={t('adv.plan.tableHelp')}
        right={<Button size="sm" icon={tableOpen ? 'chevUp' : 'chevDown'}
          aria-expanded={tableOpen} onClick={() => setTableOpen((open) => !open)}>{t(tableOpen ? 'adv.plan.hideVariance' : 'adv.plan.showVariance')}</Button>} />
      {tableOpen && <Card>
      <div className="aii-budget-table">
        <div className="aii-budget-row aii-budget-head">
          <span>{t('adv.plan.col.category')}</span>
          <span data-help-title={t('adv.plan.col.budget')} data-help={t('adv.help.budgetToDate')}>{t('adv.plan.col.budget')}</span>
          <span data-help-title={t('adv.plan.col.actual')} data-help={t('adv.help.actual')}>{t('adv.plan.col.actual')}</span>
          <span data-help-title={t('adv.plan.col.variance')} data-help={t('adv.help.variance')}>{t('adv.plan.col.variance')}</span>
        </div>
        {model.rows.map((row) => <AiBudgetRow key={row.key} row={row} monthsElapsed={model.monthsElapsed} />)}
      </div>
      <div className="row gap-8" style={{ marginTop: 14, flexWrap: 'wrap' }}>
        <Button variant="secondary" size="sm" icon="arrowRight" onClick={() => onNav && onNav('budsjett')}>{t('adv.plan.editBudget')}</Button>
      </div>
      </Card>}
    </section>
  </React.Fragment>;
}

Object.assign(window, { AiPlanTab });
